import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { RotateCw, Check } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

// Ordered transcript segments; those with a trigger are fact phrases.
const SEGMENTS = [
  { text: "Hi, I'm calling about my accident. I was " },
  { text: "rear-ended", trigger: "incident" },
  { text: " on the 405 " },
  { text: "last Tuesday", trigger: "date" },
  { text: ". The other driver — their insurance is " },
  { text: "Acme", trigger: "insurer" },
  { text: ". I was taken to " },
  { text: "St. Mary's", trigger: "provider" },
  { text: " by ambulance, they did " },
  { text: "X-rays", trigger: "records" },
  { text: ". Now my " },
  { text: "back is killing me", trigger: "symptom" },
  { text: ". The claim number they gave me is " },
  { text: "4471", trigger: "claim" },
  { text: "." },
];

// Ring order.
const NODES = [
  { id: "incident", label: "Incident", value: "Rear-end" },
  { id: "date", label: "Date", value: "Last Tues" },
  { id: "insurer", label: "Insurer", value: "Acme" },
  { id: "provider", label: "Provider", value: "St. Mary's" },
  { id: "records", label: "Records", value: "X-rays" },
  { id: "symptom", label: "Symptom", value: "Back pain" },
  { id: "claim", label: "Claim #", value: "4471" },
];

export const ContextSlide = ({ slide }) => {
  const reduce = useReducedMotion();

  const [revealed, setRevealed] = useState(0); // segments shown
  const [active, setActive] = useState({}); // node id -> true
  const [ended, setEnded] = useState(false);
  const [caseId, setCaseId] = useState("—");
  const [positions, setPositions] = useState([]);
  const [dims, setDims] = useState({ w: 0, h: 0, cx: 0, cy: 0 });

  const webRef = useRef(null);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  const activeCount = Object.values(active).filter(Boolean).length;

  const play = useCallback(() => {
    clearTimers();
    setRevealed(0);
    setActive({});
    setEnded(false);
    setCaseId("—");

    if (reduce) {
      setRevealed(SEGMENTS.length);
      setActive(Object.fromEntries(NODES.map((n) => [n.id, true])));
      setCaseId("4471");
      setEnded(true);
      return;
    }

    let delay = 1000; // after entrance
    SEGMENTS.forEach((seg, i) => {
      timers.current.push(setTimeout(() => setRevealed(i + 1), delay));
      if (seg.trigger) {
        const id = seg.trigger;
        timers.current.push(
          setTimeout(() => {
            setActive((prev) => ({ ...prev, [id]: true }));
            if (id === "claim") setCaseId("4471");
          }, delay + 140)
        );
      }
      delay += Math.max(180, seg.text.length * 38);
    });
    timers.current.push(setTimeout(() => setEnded(true), delay + 200));
  }, [reduce]);

  useEffect(() => {
    play();
    return clearTimers;
  }, [play]);

  // Ring layout, recomputed on resize.
  const compute = useCallback(() => {
    const el = webRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const cx = w / 2;
    const cy = h / 2;
    const rx = 0.36 * w;
    const ry = 0.34 * h;
    const n = NODES.length;
    const pos = NODES.map((_, i) => {
      const a = ((-90 + i * (360 / n)) * Math.PI) / 180;
      return { x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) };
    });
    setDims({ w, h, cx, cy });
    setPositions(pos);
  }, []);

  useLayoutEffect(() => {
    compute();
    const ro = new ResizeObserver(compute);
    if (webRef.current) ro.observe(webRef.current);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [compute]);

  // Entrance stagger
  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 4}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(22px,4.5%,48px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Our thesis</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            05 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-4"
        >
          Context is in the conversations.
        </motion.h1>

        {/* Subhead */}
        <motion.p
          variants={item}
          className="font-sans text-[15px] text-ink-soft leading-relaxed mt-3 max-w-[64ch]"
        >
          Every call carries the facts that move a case. Lexafide listens, pulls
          them out, and weaves them into a living case context every agent shares
          — so every next conversation is sharper than the one before.
        </motion.p>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-2 gap-5 mt-6">
          {/* LEFT — live call card */}
          <motion.div
            variants={item}
            data-testid="live-call-card"
            className="rounded-2xl border border-hairline bg-[#FBFAF6] p-5 flex flex-col min-h-0 shadow-[0_1px_3px_rgba(35,31,35,0.04),0_12px_30px_-18px_rgba(35,31,35,0.16)]"
          >
            {/* header */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[linear-gradient(140deg,#1E5BFF,#0E2E86)] flex items-center justify-center shrink-0">
                <span className="font-display font-semibold text-white text-lg">J</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-sans text-sm font-medium text-ink">Jane Doe</div>
                <div className="font-mono text-[11px] text-ink-muted">
                  Inbound call · 9:42 AM
                </div>
              </div>
              <div
                data-testid="call-status"
                className="inline-flex items-center gap-2 rounded-full border border-hairline px-2.5 py-1"
              >
                <span className="relative flex w-2 h-2">
                  {!ended ? (
                    <span className="live-ping absolute inline-flex w-full h-full rounded-full bg-accent" />
                  ) : null}
                  <span
                    className={`relative inline-flex w-2 h-2 rounded-full ${
                      ended ? "bg-ink-muted" : "bg-accent"
                    }`}
                  />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft">
                  {ended ? "Call ended" : "Listening"}
                </span>
              </div>
            </div>

            {/* transcript */}
            <div className="mt-5 flex-1 min-h-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mb-2">
                Live transcript
              </div>
              <p
                data-testid="transcript"
                className="font-sans text-[15px] leading-[1.75] text-ink-soft"
              >
                {SEGMENTS.slice(0, revealed).map((seg, i) =>
                  seg.trigger ? (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: reduce ? 0.001 : 0.35, ease: EASE }}
                      className="text-accent font-semibold underline decoration-accent/50 decoration-2 underline-offset-2"
                    >
                      {seg.text}
                    </motion.span>
                  ) : (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: reduce ? 0.001 : 0.35, ease: EASE }}
                    >
                      {seg.text}
                    </motion.span>
                  )
                )}
                {!ended && !reduce ? (
                  <span className="inline-block w-[2px] h-[1.05em] align-[-0.15em] bg-accent ml-0.5 animate-pulse" />
                ) : null}
              </p>
            </div>

            {/* footer */}
            <div className="pt-4">
              <button
                data-testid="replay-button"
                onClick={play}
                className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-muted hover:text-ink transition-colors"
              >
                <RotateCw className="w-3.5 h-3.5" />
                Replay
              </button>
            </div>
          </motion.div>

          {/* RIGHT — case context web */}
          <motion.div
            variants={item}
            data-testid="context-web"
            className="rounded-2xl p-5 flex flex-col min-h-0 text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-paper/60">
                Case context · woven live
              </span>
              <span
                data-testid="context-counter"
                className="font-mono text-sm text-[#93B4FF] tabular-nums"
              >
                {activeCount} / 7
              </span>
            </div>

            {/* web */}
            <div ref={webRef} className="relative flex-1 min-h-[240px] my-3">
              {/* connecting lines */}
              <svg className="absolute inset-0 w-full h-full" style={{ overflow: "visible" }}>
                {positions.map((p, i) => {
                  const on = active[NODES[i].id];
                  return (
                    <line
                      key={i}
                      x1={dims.cx}
                      y1={dims.cy}
                      x2={p.x}
                      y2={p.y}
                      stroke={on ? "#1E5BFF" : "rgba(147,180,255,0.22)"}
                      strokeWidth={on ? 1.5 : 1}
                      strokeDasharray={on ? "0" : "4 5"}
                      style={{ transition: "stroke 0.4s ease" }}
                    />
                  );
                })}
              </svg>

              {/* center node */}
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
                style={{ left: dims.cx, top: dims.cy }}
              >
                <div className="rounded-xl px-4 py-3 text-center bg-[linear-gradient(135deg,#1E5BFF,#6FA0FF)] shadow-[0_8px_24px_-8px_rgba(30,91,255,0.6)]">
                  <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/80">
                    Matter
                  </div>
                  <div
                    data-testid="case-id"
                    className="font-display font-semibold text-white text-base leading-tight mt-0.5"
                  >
                    Case #{caseId}
                  </div>
                  <div className="font-sans text-[11px] text-white/85">Jane Doe</div>
                </div>
              </div>

              {/* satellite nodes */}
              {positions.map((p, i) => {
                const node = NODES[i];
                const on = !!active[node.id];
                return (
                  <div
                    key={node.id}
                    data-testid={`node-${node.id}`}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 z-10 rounded-lg px-2.5 py-1.5 text-center min-w-[74px] border transition-colors duration-300 ${
                      on
                        ? "border-solid border-accent bg-accent/[0.14] node-pop"
                        : "border-dashed border-[#93B4FF]/30 bg-white/[0.02]"
                    }`}
                    style={{ left: p.x, top: p.y }}
                    data-active={on}
                  >
                    <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-[#93B4FF]">
                      {node.label}
                    </div>
                    <div
                      className={`font-sans text-[12px] font-medium text-white transition-opacity duration-500 ${
                        on ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {on ? node.value : "—"}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* footer */}
            <div
              data-testid="context-footer"
              className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 transition-opacity duration-500 ${
                ended ? "opacity-100" : "opacity-0"
              }`}
            >
              <span className="font-sans text-[12px] text-paper/70">
                Context primed for every next AI conversation.
              </span>
              <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-accent/15 border border-accent/30 px-2.5 py-1">
                <Check className="w-3 h-3 text-[#93B4FF]" strokeWidth={3} />
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#93B4FF]">
                  CRM in sync
                </span>
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Stage>
  );
};

export default ContextSlide;
